// ABOUTME: Provisioning status display component for workspaces.
// ABOUTME: Shows Service Principal and catalog status with retry functionality.

import { useState } from 'react';
import { StatusBadge } from './StatusBadge';
import { workspaceApi } from '@/services/api/adminClient';
import type { ServicePrincipalStatus } from '@/types/admin';

interface ProvisioningStatusProps {
  workspaceId: string;
  spStatus: ServicePrincipalStatus;
  spErrorMessage?: string | null;
  spCreatedAt?: string | null;
  hasWriteableCatalog: boolean;
  catalogName?: string | null;
  catalogCreatedAt?: string | null;
  onRetrySuccess: () => void;
}

export function ProvisioningStatus({
  workspaceId,
  spStatus,
  spErrorMessage,
  hasWriteableCatalog,
  catalogName,
  onRetrySuccess,
}: ProvisioningStatusProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryError(null);
    try {
      await workspaceApi.retryProvisioning(workspaceId);
      onRetrySuccess();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Retry failed';
      setRetryError(errorMessage);
      console.error('Retry failed:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  const showRetryButton = spStatus === 'failed' || (spStatus === 'active' && !hasWriteableCatalog);

  return (
    <div className="flex flex-col gap-2">
      {/* Service Principal Status */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-surface-500 w-12">SP:</span>
        <StatusBadge status={spStatus} errorMessage={spErrorMessage} />
      </div>

      {/* Catalog Status */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-surface-500 w-12">Catalog:</span>
        {hasWriteableCatalog ? (
          <StatusBadge status="active" label={catalogName || 'Active'} />
        ) : spStatus === 'active' ? (
          <StatusBadge status="failed" label="Missing" />
        ) : (
          <StatusBadge status="pending" label="Pending SP" />
        )}
      </div>

      {/* Retry button for failures */}
      {showRetryButton && (
        <div className="flex flex-col gap-1">
          <button
            onClick={handleRetry}
            disabled={isRetrying}
            className="text-xs text-primary-600 hover:underline disabled:opacity-50 text-left cursor-pointer"
          >
            {isRetrying ? 'Retrying...' : 'Retry Provisioning'}
          </button>
          {retryError && (
            <span className="text-xs text-error">{retryError}</span>
          )}
        </div>
      )}
    </div>
  );
}
